import { faFileExport, faPlus, faColumns, faAngleDown, faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery, useLazyQuery, useMutation } from "@apollo/client";
import { ChangeEvent, useCallback, useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dropdown, Spinner, Modal } from "react-bootstrap"; // Import Dropdown and Modal
import Badge, { BadgeBg } from "components/base/Badge"; // Import Badge from custom components
import CommissionDetailsModal, { ProductCommissionDetails, ShippingMethod, CommissionCalculation } from "components/modals/CommissionDetailsModal"; // Import CommissionDetailsModal
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "components/custom/toastify-custom.css";
// Existing imports
import PageBreadcrumb from "components/common/PageBreadcrumb";
import FilterTab, { FilterTabItem } from "components/common/FilterTab";
import FilterButtonGroup, { FilterMenu } from "components/common/FilterButtonGroup";
import SearchBox from "components/common/SearchBox";
import Button from "components/base/Button";
import ColumnVisibilityToggle from "components/common/ColumnVisibilityToggle";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/products/useAdvanceTable";

// Table and columns
import ProductsTablecolumns, { productsTablecolumns } from "components/tables/ProductsTable";
// GraphQL Query and Mutations
import { SELLER_PRODUCTS_QUERY } from "api/graphql/queries/product/GetProductList";
import { UPDATE_PRODUCT } from "api/graphql/mutations/product/UpdateProduct";
// Import fallback image
import fallbackImage from "assets/img/img-default/Empty state.jpg";
import { Column, PaginationState, VisibilityState } from "@tanstack/react-table";
// import { ProductsTableProductType, Status, ShippingMethod } from "./Products.types";

export type ProductsTableProductType = {
  id: string; // ✅ Unique identifier
  sku: string; // ✅ Product SKU
  product: string; // ✅ Product name
  productImage: string; // ✅ Primary product image
  type: string; // ✅ Product type
  sellerReference?: string;
  price: number; // ✅ Product price
  status: number; // ✅ Product Approval status
  product_status: number; // ✅ Product Catalog status
  visibility: number; // ✅ Visibility  
  weight: string | number; // ✅ Weight (can be numeric or string)
  stock: number; // ✅ Stock quantity
  isInStock: boolean; // ✅ Stock availability
  salable_qty: number; // ✅ Salable quantity
  shippingMethod: number | string; // ✅ Shipping method
  fixCommission: number; // ✅ Fixed commission amount
  percentCommission: number; // ✅ Percentage commission
  specialPrice?: number; // ✅ Special discount price
  discountStartDate?: string | null; // ✅ Discount start date
  discountEndDate?: string | null; // ✅ Discount end date
  brand: string; // ✅ Product brand (default: Lenovo)
  dimensions: { // ✅ Product dimensions
    longueur: string | number;
    largeur: string | number;
    hauteur: string | number;
  };
  priceMin?: number;
  priceMax?: number;
  category: Array<{ id: string; name: string }>; // ✅ Categories as an array of objects
  variant?: number | string // ✅ Variants as an array of objects
  deactivation_reason?: { reason: string[]; description: string }; // Deactivation reasons
  // tags?: string[];
  // starred: boolean; // ✅ Favorite product flag
  // publishedOn: string; // ✅ Published date
};

export enum Status {
  All = "all",
  Approved = 1,
  Disapproved = 0,
  Rejected = 2
}
// Export ShippingMethod from CommissionDetailsModal
export { ShippingMethod } from "components/modals/CommissionDetailsModal";

// Utility function for safe date formatting without timezone conversion
export const formatDateForInput = (dateString: string | null | undefined): string => {
  if (!dateString) return "";

  // Parse the date and format it as YYYY-MM-DD without timezone conversion
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const roundFixCommission = (value: number): number => {
  if (!value) return 0; // ✅ Ensure it always returns a number

  const thirdDigit = Math.floor((value * 1000) % 10); // ✅ Extract third decimal place
  const roundedValue = thirdDigit >= 5
    ? Math.ceil(value * 100) / 100
    : Math.floor(value * 100) / 100;

  return Number(roundedValue.toFixed(2)); // ✅ Ensure it's a number
};

const transformSellerProducts = (sellerProducts: any[]): ProductsTableProductType[] => {
  return sellerProducts.map((item, index) => {
    
    // Example logic for prices
    return {
      id: item.entityid || `unknown-${index}`,
      sku: item.sku || "N/A",
      sellerReference: item.seller_reference || "-" ,
      product: item.name || "Unnamed Product",
      productImage: item.image1?.url || fallbackImage,
      type: item.type_id || "N/A",
      price: item.prices || 0,
      status: item.status || "-",
      product_status: item.product_status || "-",
      visibility: item.visibility || "-",
      weight: item.weight ?? "-",
      stock: item.stock_data?.qty ?? 0, // ✅ Ensure stock exists
      salable_qty: item.stock_data?.salable_qty ?? 0, // ✅ Ensure salable_qty exists
      isInStock: item.stock_data?.is_in_stock ?? false,
      shippingMethod: Number(item.mode_expedition) || "Unknown", // ✅ Convert to number
      percentCommission: Math.trunc(item.percent_commission_for_product || 0), // ✅ Prevent NaN
      fixCommission: roundFixCommission(item.commission_for_product || 0), // ✅ Apply rounding
      specialPrice: item.specialprice || null,
      discountStartDate: item.start_date || null,
      discountEndDate: item.end_date || null,
      brand: item.marque || "-", // Placeholder if no brand is provided
      dimensions: {
        longueur: Math.trunc(item.longueur25) || "-",
        largeur: Math.trunc(item.dimensions_de_l_outil_largeur1) || "-",
        hauteur: Math.trunc(item.dimensions_de_l_outil_hauteur1) || "-",
      },
      category: item.sellercategories,
      variant: item.variants_count || "-",
      deactivation_reason: item.deactivation_reason || [],
      starred: false,
      publishedOn: "-",
    };
  });
};

const Products = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Toast configuration
  const notifyError = (message: string) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };
  
  const notifySuccess = (message: string) => {
    toast.success(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };
  
  // Get location to check for filters from navigation
  const location = useLocation();
  const filterByOutOfStock = location.state?.filterByOutOfStock || false;
  
  // Get navigation state filters
  const navigationFilters = location.state?.filter || {};
  const navigationPage = location.state?.page || 1;
  const navigationPageSize = location.state?.pageSize || 10;

  // Modal state for deactivation reason details
  const [showModal, setShowModal] = useState(false);
  const [currentReasonDetails, setCurrentReasonDetails] = useState<{ reason: string[]; description: string } | null>(null);
  
  // Modal state for commission details
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [currentCommissionDetails, setCurrentCommissionDetails] = useState<ProductsTableProductType | null>(null);
  
  // Inline editing state for selected products
  const [selectedRows, setSelectedRows] = useState<{ [key: string]: ProductsTableProductType }>({});
  const [editedProducts, setEditedProducts] = useState<{ [key: string]: { [field: string]: any } }>({});
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);

  // Handler for showing deactivation reason details in modal
  const handleShowReasonDetails = (details: { reason: string[]; description: string }) => {
    setCurrentReasonDetails(details);
    setShowModal(true);
  };

  // Handler for closing the modal
  const handleCloseModal = () => {
    setShowModal(false);
  };
  
  // Handler for showing commission details in modal
  const handleShowCommissionDetails = (product: ProductsTableProductType) => {
    setCurrentCommissionDetails(product);
    setShowCommissionModal(true);
  };

  // Handler for closing the commission modal
  const handleCloseCommissionModal = () => {
    setShowCommissionModal(false);
  };
  
  // Handler for row selection
  const handleRowSelection = (selectedRowIds: Record<string, boolean>) => {
    // Convert selected row IDs to an object with the product data
    const selectedProducts: { [key: string]: ProductsTableProductType } = {};
    Object.keys(selectedRowIds).forEach(id => {
      if (selectedRowIds[id]) {
        const product = tableData.find(p => p.id === id);
        if (product) {
          selectedProducts[id] = product;
        }
      }
    });
    setSelectedRows(selectedProducts);
  };

  // Handler for field changes in selected rows
  const handleFieldChange = (productId: string, field: string, value: any) => {
    setEditedProducts(prev => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || {}),
        [field]: value
      }
    }));
  };
  
  // State for inline editing
  const [editingCell, setEditingCell] = useState<string | undefined>(undefined);
  const [editingRow, setEditingRow] = useState<string | undefined>(undefined); // Track which row is being edited
  const [editValue, setEditValue] = useState<string | number>("");
  const [editSpecialPrice, setEditSpecialPrice] = useState<string | number>("");
  const [editStartDate, setEditStartDate] = useState<string>("");
  const [editEndDate, setEditEndDate] = useState<string>("");
  
  // Handler for inline edit actions
  const handleInlineEdit = async (action: string, data?: any) => {
    if (action === "startEdit") {
      // Start editing a cell
      setEditingCell(data.cell);
      setEditValue(data.value || "");
      
      // If we're editing dates, set those too
      if (data.startDate) {
        setEditStartDate(data.startDate);
      }
      if (data.endDate) {
        setEditEndDate(data.endDate);
      }
    }
    else if (action === "startRowEdit") {
      // Start editing an entire row's pricing fields
      const product = tableData.find(p => p.id === data.id);
      if (product) {
        // Clear any existing cell editing
        setEditingCell(undefined);
        
        // Set row editing mode
        setEditingRow(product.id);
        
        // Set all price-related values
        setEditValue(product.price || "");
        setEditSpecialPrice(product.specialPrice || "");
        setEditStartDate(formatDateForInput(product.discountStartDate));
        setEditEndDate(formatDateForInput(product.discountEndDate));
          
        console.log('Starting row edit for product:', {
          id: product.id,
          price: product.price,
          specialPrice: product.specialPrice,
          discountStartDate: product.discountStartDate,
          discountEndDate: product.discountEndDate
        });
      }
    }
    else if (action === "editValue") {
      // Update edit value while editing
      setEditValue(data);
    }
    else if (action === "editSpecialPrice") {
      // Update special price value while editing
      setEditSpecialPrice(data);
      console.log("Updated special price to:", data);
    }
    else if (action === "editStartDate") {
      // Update start date while editing
      setEditStartDate(data);
    }
    else if (action === "editEndDate") {
      // Update end date while editing
      setEditEndDate(data);
    }
    else if (action === "cancelEdit") {
      // Cancel editing
      setEditingCell(undefined);
      setEditingRow(undefined);
      setEditValue("");
      setEditSpecialPrice("");
      setEditStartDate("");
      setEditEndDate("");
    }
    else if (action === "saveEdit") {
      // Save edited value
      setUpdateLoading(true);
      
      try {
        const productId = data.id;
        const field = data.field;
        
        // Prepare input for the mutation
        const input: any = {
          productId: productId,
          productData: {}
        };
        
        // Validation checks
        let hasValidationError = false;
        
        // Process the field being edited
        if (field === "price") {
          const price = parseFloat(data.value);
          // Validate price is greater than 0
          if (price <= 0) {
            notifyError(t('products.validation.priceGreaterThanZero', 'Price must be greater than 0'));
            hasValidationError = true;
          } else {
            input.productData.price = price;
          }
        }
        else if (field === "rowPricing") {
          // Validate regular price
          const price = parseFloat(data.price);
          if (price <= 0) {
            notifyError(t('products.validation.priceGreaterThanZero', 'Price must be greater than 0'));
            hasValidationError = true;
          } else {
            // Save both price and special price in one go
            input.productData.price = price;
          }
          
          // Validate special price if provided
          if (data.specialPrice) {
            const specialPrice = parseFloat(data.specialPrice);
            if (specialPrice <= 0) {
              notifyError(t('products.validation.specialPriceGreaterThanZero', 'Special price must be greater than 0'));
              hasValidationError = true;
            } else if (!data.startDate || !data.endDate) {
              notifyError(t('products.validation.dateRangeRequired', 'Date range is required when special price is provided'));
              hasValidationError = true;
            } else {
              // Add special price data if it exists and validation passed
              input.productData.special_price = {
                value: specialPrice,
                from_date: data.startDate,
                end_date: data.endDate
              };
            }
          }
        }
        else if (field === "specialPrice") {
          // Validate special price
          const specialPrice = parseFloat(data.value || 0);
          if (specialPrice <= 0) {
            notifyError(t('products.validation.specialPriceGreaterThanZero', 'Special price must be greater than 0'));
            hasValidationError = true;
          } else if (!data.startDate || !data.endDate) {
            notifyError(t('products.validation.dateRangeRequired', 'Date range is required when special price is provided'));
            hasValidationError = true;
            } else {
              // Handle special price and date range together
              input.productData.special_price = {
                value: specialPrice,
                from_date: data.startDate ? formatDateForInput(data.startDate) : formatDateForInput(new Date().toISOString()),
                end_date: data.endDate ? formatDateForInput(data.endDate) : formatDateForInput(new Date().toISOString())
              };
            }
        }
        else if (field === "discountStartDate") {
          // We need to get the current special price and end date to maintain them
          const product = tableData.find(p => p.id === productId);
          
          if (product) {
            const specialPrice = product.specialPrice || 0;
            
            // Validate special price exists
            if (specialPrice <= 0) {
              notifyError(t('products.validation.specialPriceRequired', 'Special price is required when setting a date range'));
              hasValidationError = true;
            } else {
              input.productData.special_price = {
                value: specialPrice,
                from_date: data.value ? formatDateForInput(data.value) : formatDateForInput(new Date().toISOString()),
                end_date: formatDateForInput(product.discountEndDate) || formatDateForInput(new Date().toISOString())
              };
            }
          }
        }
        else if (field === "discountEndDate") {
          // We need to get the current special price and start date to maintain them
          const product = tableData.find(p => p.id === productId);
          
          if (product) {
            const specialPrice = product.specialPrice || 0;
            
            // Validate special price exists
            if (specialPrice <= 0) {
              notifyError(t('products.validation.specialPriceRequired', 'Special price is required when setting a date range'));
              hasValidationError = true;
            } else {
              input.productData.special_price = {
                value: specialPrice,
                from_date: formatDateForInput(product.discountStartDate) || formatDateForInput(new Date().toISOString()),
                end_date: data.value ? formatDateForInput(data.value) : formatDateForInput(new Date().toISOString())
              };
            }
          }
        }
        
        // Skip mutation if validation failed
        if (hasValidationError) {
          setUpdateLoading(false);
          return;
        }
        
        // Execute the mutation
        const { data: responseData } = await updateProduct({
          variables: { input }
        });
        
        if (!responseData?.updateProduct?.success) {
          console.error(`Failed to update product ${productId}:`, responseData?.updateProduct?.message);
          notifyError(responseData?.updateProduct?.message || t('products.updateFailed', 'Failed to update product'));
        } else {
          // Success notification
          notifySuccess(t('products.updateSuccess', 'Product updated successfully'));
          
          // Refresh product list
          fetchProducts({
            variables: {
              page: pagination.pageIndex + 1,
              pageSize: pagination.pageSize,
              filter: getActiveFilters(),
            },
          });
        }
        
        // Clear editing state
        setEditingCell(undefined);
        setEditingRow(undefined);
        setEditValue("");
        setEditSpecialPrice("");
        setEditStartDate("");
        setEditEndDate("");
        
      } catch (error) {
        console.error("Error updating product:", error);
        notifyError(t('products.updateError', 'An error occurred while updating the product'));
      } finally {
        setUpdateLoading(false);
      }
    }
  };
  
  // Calculate the total commission (works with both product types)
  const calculateTotalCommission = (product: ProductsTableProductType | ProductCommissionDetails): CommissionCalculation => {
    const fixCommission = product.fixCommission || 0;
    const percentCommission = (product.percentCommission || 0) / 100;
    
    // Determine if special price applies based on current date and special price date range
    const currentDate = new Date();
    const specialPriceStartDate = product.discountStartDate ? new Date(product.discountStartDate) : null;
    const specialPriceEndDate = product.discountEndDate ? new Date(product.discountEndDate) : null;
    
    // Need to safely check if specialPrice exists and is greater than 0
    const hasValidSpecialPrice = 
      product.specialPrice !== undefined && 
      product.specialPrice !== null && 
      Number(product.specialPrice) > 0;
    
    const isSpecialPriceApplicable = Boolean(
      specialPriceStartDate && 
      specialPriceEndDate && 
      currentDate >= specialPriceStartDate && 
      currentDate <= specialPriceEndDate && 
      hasValidSpecialPrice
    );
    
    // Use special price if applicable, otherwise use regular price
    const applicablePrice = isSpecialPriceApplicable 
      ? Number(product.specialPrice || 0)
      : Number(product.price || 0);
    
    const percentCommissionValue = applicablePrice * percentCommission;
    const totalCommission = fixCommission + percentCommissionValue;
    
    return {
      fixCommission,
      percentCommission: product.percentCommission || 0,
      percentCommissionValue: parseFloat(percentCommissionValue.toFixed(2)),
      applicablePrice,
      totalCommission: parseFloat(totalCommission.toFixed(2)),
      isSpecialPriceApplicable
    };
  };

  const [tableData, setTableData] = useState<ProductsTableProductType[]>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [paginationCount, setPaginationCount] = useState(
    {
      totalPages: 0,
      totalCount: 0
    }
  )
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: navigationPage - 1,
    pageSize: navigationPageSize,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState("");
  // State for active filter labels
  const [activeShippingMethodLabel, setActiveShippingMethodLabel] = useState<string>(t("products.shippingMethod"));
  const [activeSituationLabel, setActiveSituationLabel] = useState<string>(t("products.situation"));
  const [activeProductTypeLabel, setActiveProductTypeLabel] = useState<string>(
    navigationFilters.type_id === 'simple' ? t("products.simple") :
    navigationFilters.type_id === 'configurable' ? t("products.configurable") :
    t("products.productType")
  );

  // State for active filters
  const [activeShippingMethodFilter, setShippingMethodFilter] = useState<number | "all">("all");
  const [activeStatusFilter, setActiveStatusFilter] = useState<number | "all">("all");
  const [activeProductTypeFilter, setActiveProductTypeFilter] = useState<string | "all">(
    navigationFilters.type_id || "all"
  );
  // State for Approval Status Filter
  // Initialize approval status from navigation filters
  const initialApprovalStatus = navigationFilters.product_status !== undefined ? navigationFilters.product_status : "all";
  const [activeApprovalStatusFilter, setActiveApprovalStatusFilter] = useState<number | "all">(initialApprovalStatus);
  const [activeApprovalStatusLabel, setActiveApprovalStatusLabel] = useState<string>(
    initialApprovalStatus === 1 ? t("approved") :
    initialApprovalStatus === 2 ? t("disapproved") :
    initialApprovalStatus === 3 ? t("rejected") :
    t("products.approvalStatus")
  );
  // State for quantity range filter
  const [qtyFromValue, setQtyFromValue] = useState<string>(navigationFilters.qty_from?.toString() || "");
  const [qtyToValue, setQtyToValue] = useState<string>(navigationFilters.qty_to?.toString() || "");
  const [qtyRangeApplied, setQtyRangeApplied] = useState<boolean>(
    !!(navigationFilters.qty_from || navigationFilters.qty_to)
  );

  // Type definition for filter properties
  interface ProductFilters {
    name?: string;
    mode_expedition?: number;
    status?: number;
    product_status?: number;
    qty?: number;
    qty_from?: number;
    qty_to?: number;
    [key: string]: any; // Allow additional properties
  }

  // Function to build active filters for API calls
  const getActiveFilters = (includeNavigationFilters = false): ProductFilters => {
    const filters: ProductFilters = {
      ...submittedSearchTerm && { name: submittedSearchTerm },
      ...activeShippingMethodFilter !== "all" && { mode_expedition: activeShippingMethodFilter },
      ...activeStatusFilter !== "all" && { status: activeStatusFilter },
      ...activeApprovalStatusFilter !== "all" && { product_status: activeApprovalStatusFilter },
      ...activeProductTypeFilter !== "all" && { type_id: activeProductTypeFilter },
    };

    // Add quantity range filter if applied
    if (qtyRangeApplied) {
      if (qtyFromValue) {
        filters.qty_from = parseInt(qtyFromValue);
      }
      if (qtyToValue) {
        filters.qty_to = parseInt(qtyToValue);
      }
    }
    
    // Include navigation filters on initial load
    if (includeNavigationFilters && Object.keys(navigationFilters).length > 0) {
      return { ...filters, ...navigationFilters };
    }

    return filters;
  };

  // Add the update product mutation
  const [updateProduct] = useMutation(UPDATE_PRODUCT);
  
  const [fetchProducts, { loading, error, data }] = useLazyQuery(SELLER_PRODUCTS_QUERY, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  // Initial fetch on component mount with navigation filters
  useEffect(() => {
    // Apply navigation filters to component state
    let shouldFetchProducts = false;
    
    // Handle quantity range filter from navigation
    if (navigationFilters.qty_from || navigationFilters.qty_to) {
      if (navigationFilters.qty_from) {
        setQtyFromValue(navigationFilters.qty_from.toString());
      }
      if (navigationFilters.qty_to) {
        setQtyToValue(navigationFilters.qty_to.toString());
      }
      setQtyRangeApplied(true);
      shouldFetchProducts = true;
    }
    
    // Handle product type filter from navigation
    if (navigationFilters.type_id) {
      setActiveProductTypeFilter(navigationFilters.type_id);
      if (navigationFilters.type_id === 'simple') {
        setActiveProductTypeLabel(t("products.simple", "Simple"));
      } else if (navigationFilters.type_id === 'configurable') {
        setActiveProductTypeLabel(t("products.configurable", "Configurable"));
      }
      shouldFetchProducts = true;
    }
    
    // Handle product_status filter from navigation
    if (navigationFilters.product_status !== undefined) {
      setActiveApprovalStatusFilter(navigationFilters.product_status);
      
      // Set the appropriate label based on the status
      if (navigationFilters.product_status === 1) {
        setActiveApprovalStatusLabel(t("approved"));
      } else if (navigationFilters.product_status === 2) {
        setActiveApprovalStatusLabel(t("disapproved"));
      } else if (navigationFilters.product_status === 3) {
        setActiveApprovalStatusLabel(t("rejected"));
      }
      
      shouldFetchProducts = true;
    }
    
    // Handle status filter from navigation (for orders)
    if (navigationFilters.status) {
      if (navigationFilters.status === 'processing') {
        // This is for new orders (awaiting processing)
        setActiveStatusFilter(1); // Assuming 1 is for "On Sale" status
        setActiveSituationLabel(t("onSale"));
      } else if (navigationFilters.status === 'complete') {
        // This is for complete orders
        setActiveStatusFilter(1); // Assuming 1 is for active/complete status
        setActiveSituationLabel(t("onSale"));
      } else if (navigationFilters.status === 'ready_to_return') {
        // This is for ready to return orders
        // You might need to map this to a different filter
      }
      shouldFetchProducts = true;
    }
    
    // Only fetch if we have navigation filters, otherwise let the other useEffect handle it
    if (shouldFetchProducts || Object.keys(navigationFilters).length > 0) {
      setTimeout(() => {
        fetchProducts({
          variables: {
            page: navigationPage,
            pageSize: navigationPageSize,
            filter: getActiveFilters(true),
          },
        });
        
        // Clear navigation state to prevent filters from persisting on refresh
        navigate('/products', { replace: true, state: {} });
      }, 100); // Small delay to ensure state updates are processed
    }
  }, []); // Run only on mount

  // Handle initial stock filter from navigation
  useEffect(() => {
    if (filterByOutOfStock) {
      // Reset to first page
      setPagination(prev => ({
        ...prev,
        pageIndex: 0
      }));
      
      // Fetch products with filter
      fetchProducts({
        variables: {
          page: 1,
          pageSize: pagination.pageSize,
          filter: {
            ...getActiveFilters(),
            qty: 0
          },
        },
      });
    }
  }, [filterByOutOfStock]);
 
  // Pagination Handlers
  const handlePageChange = (newPageIndex: number) => {
    // Calculate the safe page index (prevent out-of-bounds)
    const maxPageIndex = Math.max(0, paginationCount.totalPages - 1);
    const safePageIndex = Math.min(newPageIndex, maxPageIndex);
    
    // Update the pagination state
    setPagination((prev) => ({
      ...prev,
      pageIndex: safePageIndex,
    }));
    
    // Calculate the API page number (1-based)
    const apiPageNumber = safePageIndex + 1;
    
    // Fetch products with the new page
    fetchProducts({
      variables: {
        page: apiPageNumber,
        pageSize: pagination.pageSize,
        filter: getActiveFilters(),
      },
    });
  };
  
  // Handle page size changes
  const handlePageSizeChange = (newPagination: PaginationState) => {
    console.log("Page size changed to:", newPagination.pageSize);
    
    // Update the pagination state
    setPagination(newPagination);
    
    // Always reset to first page when page size changes
    fetchProducts({
      variables: {
        page: 1, // Reset to first page
        pageSize: newPagination.pageSize,
        filter: getActiveFilters(),
      },
    });
  };
  // Shipping Method Filter Handler
  const setShippingMethodActiveFilter = (modeExpedition: number | null, label: string) => {
    // Update filter state
    setShippingMethodFilter(modeExpedition ?? "all");
    setActiveShippingMethodLabel(label);
    
    // Reset to first page when filter changes
    setPagination(prev => ({
      ...prev,
      pageIndex: 0
    }));
    
    // Fetch with updated filter and reset to page 1
    fetchProducts({
      variables: {
        page: 1, // Always start at page 1 when filter changes
        pageSize: pagination.pageSize,
        filter: {
          ...getActiveFilters(),
          mode_expedition: modeExpedition,
        },
      },
    });
  };

  // Status Filter Handler
  const setStatusActiveFilter = (status: number | null, label: string) => {
    // Update filter state
    setActiveStatusFilter(status ?? "all");
    setActiveSituationLabel(label);
    
    // Reset to first page when filter changes
    setPagination(prev => ({
      ...prev,
      pageIndex: 0
    }));
    
    // Fetch with updated filter and reset to page 1
    fetchProducts({
      variables: {
        page: 1, // Always start at page 1 when filter changes
        pageSize: pagination.pageSize,
        filter: {
          ...getActiveFilters(),
          status: status,
        },
      },
    });
  };

  // Product Type Filter Handler
  const setProductTypeActiveFilter = (type_id: string | null, label: string) => {
    // Update filter state
    setActiveProductTypeFilter(type_id ?? "all");
    setActiveProductTypeLabel(label);
    
    // Reset to first page when filter changes
    setPagination(prev => ({
      ...prev,
      pageIndex: 0
    }));
    
    // Fetch with updated filter and reset to page 1
    fetchProducts({
      variables: {
        page: 1, // Always start at page 1 when filter changes
        pageSize: pagination.pageSize,
        filter: {
          ...getActiveFilters(),
          type_id: type_id,
        },
      },
    });
  };

  // Filter Handler for Approval Status
  const setApprovalStatusActiveFilter = (product_status: number | null, label: string) => {
    // Reset all filters when approval status filter is used
    setActiveApprovalStatusFilter(product_status ?? "all");
    setActiveApprovalStatusLabel(label);
    
    // Reset other filters
    setShippingMethodFilter("all");
    setActiveShippingMethodLabel("Shipping Method");
    setActiveStatusFilter("all");
    setActiveSituationLabel("Situation");
    setSearchTerm("");
    setSubmittedSearchTerm("");
    
    // No direct fetchProducts call here - the useEffect will handle it
    // This prevents sending multiple queries
  };

  // Updated filterMenus with dynamic labels - removed stock status filter
  const filterMenus: FilterMenu[] = [
    {
      label: activeShippingMethodLabel, // Dynamic label based on selection
      items: [
        { label: t("shippingMethodType.esw"), onClick: () => setShippingMethodActiveFilter(ShippingMethod.ESW, t("shippingMethodType.esw")) },
        { label: t("shippingMethodType.ess"), onClick: () => setShippingMethodActiveFilter(ShippingMethod.ESS, t("shippingMethodType.ess")) },
        { label: t("shippingMethodType.fbw"), onClick: () => setShippingMethodActiveFilter(ShippingMethod.FBW, t("shippingMethodType.fbw")) },
        {
          label: t("all"), onClick: () => {
            setShippingMethodActiveFilter(null, t("products.shippingMethod")); // Reset filter and label
          }
        },
      ]
    },
    {
      label: activeSituationLabel, // Dynamic label based on selection
      items: [
        { label: t("onSale"), onClick: () => setStatusActiveFilter(1, t("onSale")) },
        { label: t("closeForSale"), onClick: () => setStatusActiveFilter(2, t("closeForSale")) },
        {
          label: t("all"), onClick: () => {
            setStatusActiveFilter(null, t("products.situation")); // Reset filter and label
          }
        },
      ]
    },
    {
      label: activeProductTypeLabel, // Dynamic label for product type
      items: [
        { label: t("products.simple", "Simple"), onClick: () => setProductTypeActiveFilter('simple', t("products.simple", "Simple")) },
        { label: t("products.configurable", "Configurable"), onClick: () => setProductTypeActiveFilter('configurable', t("products.configurable", "Configurable")) },
        {
          label: t("all"), onClick: () => {
            setProductTypeActiveFilter(null, t("products.productType", "Product Type")); // Reset filter and label
          }
        },
      ]
    }
  ];

  const approvedCount = data?.sellerProducts?.product_status?.approved_count || 0;
  const disapprovedCount = data?.sellerProducts?.product_status?.rejected_count || 0;
  const pendingCount = data?.sellerProducts?.product_status?.disapproved_count || 0;
  const totalCount = approvedCount + disapprovedCount + pendingCount;

  const tabItems: FilterTabItem[] = [
    { label: t("all"), value: "all", count: totalCount, onClick: () => setApprovalStatusActiveFilter(null, t("all")) },
    { label: t("approved"), value: "approved", count: approvedCount, onClick: () => setApprovalStatusActiveFilter(1, t("approved")) },
    { label: t("disapproved"), value: "pending", count: pendingCount, onClick: () => setApprovalStatusActiveFilter(2, t("disapproved")) },
    { label: t("rejected"), value: "rejected", count: disapprovedCount, onClick: () => setApprovalStatusActiveFilter(3, t("rejected")) },
  ];


  const others: any = {};
  useEffect(() => {
    if (data?.sellerProducts?.products.length > 0) {
      const transformed = transformSellerProducts(data.sellerProducts.products);
      setTableData(transformed);
  
      // ✅ Update pagination counts safely
      setPaginationCount((prev) => {
        const { total_count, total_pages } = data.sellerProducts.pagination;
  
        const safeTotalPages = Math.max(1, total_pages || 1); // ✅ Ensure totalPages is always at least 1
  
        if (prev.totalCount !== total_count || prev.totalPages !== total_pages) {
          return {
            totalCount: total_count ?? 0,
            totalPages: safeTotalPages,
          };
        }
        return prev;
      });
  
    } else {
      setTableData([]);
    }
  }, [data]);
  

  const table = useAdvanceTable({
    data: tableData,
    columns: productsTablecolumns,
    pageSize: pagination.pageSize, // Use the current pagination.pageSize instead of hardcoding to 10
    pagination: true,
    sortable: true,
    rowCount: paginationCount.totalCount ?? 0,
    manualPagination: true,
    onPaginationChange: (newPagination) => {
      // If only the page index changed
      if (newPagination.pageSize === pagination.pageSize) {
        handlePageChange(newPagination.pageIndex);
      } else {
        // If the page size changed
        handlePageSizeChange(newPagination as PaginationState);
      }
    },
    selection: true,
    initialState: { columnVisibility }, // Manage column visibility stat
    onColumnVisibilityChange: setColumnVisibility
  });
  
  // Track row selection changes and manually handle row selection
  useEffect(() => {
    const selectedRowIds = table.getState().rowSelection || {};
    
    // Convert selected row IDs to an object with the product data
    const selectedProducts: { [key: string]: ProductsTableProductType } = {};
    
    // Get all selected rows from the table
    const rows = table.getRowModel().rows;
    
    // For each row index in the selection state
    Object.keys(selectedRowIds).forEach(rowIndex => {
      if (selectedRowIds[rowIndex]) {
        // Get the row at this index
        const row = rows[parseInt(rowIndex)];
        
        if (row && row.original) {
          const product = row.original as ProductsTableProductType;
          // Store both by row index and by product ID for maximum compatibility
          selectedProducts[rowIndex] = product;
          selectedProducts[product.id] = product;
        }
      }
    });
    
    console.log("Selected row IDs:", selectedRowIds);
    console.log("Selected products:", selectedProducts);
    console.log("Table row model:", rows.map(r => ({ index: r.index, id: r.original.id })));
    
    setSelectedRows(selectedProducts);
  }, [table.getState().rowSelection]);
  
  // Add a manual selection handler for checkboxes
  const handleCheckboxChange = (productId: string, checked: boolean) => {
    // Update the table's selection state
    const newRowSelection = {...table.getState().rowSelection};
    if (checked) {
      newRowSelection[productId] = true;
    } else {
      delete newRowSelection[productId];
    }
    
    // Update table state
    table.setRowSelection(newRowSelection);
    
    // Update our selected rows state directly
    const updatedSelectedRows = {...selectedRows};
    if (checked) {
      const product = tableData.find(p => p.id === productId);
      if (product) {
        updatedSelectedRows[productId] = product;
      }
    } else {
      delete updatedSelectedRows[productId];
    }
    
    setSelectedRows(updatedSelectedRows);
  };

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize }); // ✅ Always reset to first page
    table.setPageIndex(0);
  
    fetchProducts({
      variables: {
        page: 1,
        pageSize: pagination.pageSize,
        filter: getActiveFilters(),
      },
    });
  }, [activeShippingMethodFilter, activeStatusFilter, activeApprovalStatusFilter, submittedSearchTerm, qtyRangeApplied, activeProductTypeFilter]);
  
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value); // Update the searchTerm as the user types
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent form submission
  
      // Store the search term for filter use
      setSubmittedSearchTerm(searchTerm);
      
      // Reset to first page when search is performed
      setPagination(prev => ({
        ...prev,
        pageIndex: 0
      }));
      
      // Update the table UI to show first page
      table.setPageIndex(0);
  
      // Fetch with search term and reset to page 1
      fetchProducts({
        variables: {
          page: 1, // Always start at page 1 when search changes
          pageSize: pagination.pageSize,
          filter: {
            ...getActiveFilters(),
            name: searchTerm || undefined, // Remove filter if empty
          },
        },
      });
    }
  };

  return (
    <div>
      {/* Toast container for notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        className="toastify-container"
        toastClassName="toastify-toast"
      />
      
      {/* Modal for Deactivation Reason Details */}
      <Modal show={showModal} onHide={handleCloseModal} centered className="deactivation-reasons-modal">
        <Modal.Header closeButton className="bg-body-tertiary">
          <Modal.Title className="text-body">
            <FontAwesomeIcon icon={faFileExport} className="me-2" />
            {t('products.deactivationReasons', 'Deactivation Reasons')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {currentReasonDetails && currentReasonDetails.reason && currentReasonDetails.reason.length > 0 ? (
            <div>
              <div className="mb-4">
                <h5 className="text-body mb-3 border-bottom pb-2">
                  <strong>{t('products.reasonsForDeactivation', 'Reasons for Deactivation')}:</strong>
                </h5>
                <div className="mb-4">
                  {currentReasonDetails.reason.map((reason, index) => (
                    <div key={index} className="d-flex align-items-center mb-2">
                      <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-3" 
                        style={{ width: '28px', height: '28px', fontSize: '14px' }}>
                        {index + 1}
                      </div>
                      <div className="p-2 bg-body-tertiary rounded w-100">
                        <span className="fw-semibold">{reason}</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {currentReasonDetails.description && (
                  <div className="mt-4">
                    <h5 className="text-body mb-3 border-bottom pb-2">
                      <strong>{t('products.additionalDetails', 'Additional Details')}:</strong>
                    </h5>
                    <div className="p-3 bg-body-tertiary rounded border">
                      <p className="mb-0 text-body">{currentReasonDetails.description}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-5">
              <div className="text-muted mb-3">
                <span className="fa-stack fa-2x">
                  <FontAwesomeIcon icon={faFileExport} className="text-secondary opacity-25 fa-stack-2x" />
                  <FontAwesomeIcon icon={faAngleDown} className="text-secondary fa-stack-1x" />
                </span>
              </div>
              <p className="fs-5 text-body">{t('products.noDeactivationReasons', 'No deactivation reasons available')}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-body-tertiary">
          <Button variant="outline-secondary" className="px-4" onClick={handleCloseModal}>
            {t('common.close', 'Close')}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Commission Details Modal */}
      <CommissionDetailsModal
        show={showCommissionModal}
        onHide={handleCloseCommissionModal}
        product={currentCommissionDetails ? {
          id: currentCommissionDetails.id,
          name: currentCommissionDetails.product,
          sku: currentCommissionDetails.sku,
          price: currentCommissionDetails.price,
          specialPrice: currentCommissionDetails.specialPrice,
          discountStartDate: currentCommissionDetails.discountStartDate,
          discountEndDate: currentCommissionDetails.discountEndDate,
          status: currentCommissionDetails.status,
          product_status: currentCommissionDetails.product_status,
          productStatus: currentCommissionDetails.product_status,
          productImage: currentCommissionDetails.productImage,
          shippingMethod: Number(currentCommissionDetails.shippingMethod), // Cast to number
          dimensions: currentCommissionDetails.dimensions,
          weight: currentCommissionDetails.weight,
          totalSold: currentCommissionDetails.stock, // using stock as totalSold
          fixCommission: currentCommissionDetails.fixCommission, // Add the fix commission value
          percentCommission: currentCommissionDetails.percentCommission // Add the percent commission value
        } : null}
        calculateCommission={calculateTotalCommission}
      />
      
      <div className="mb-9">
        <h2 className="mb-4">{t("products.title")}</h2>
        <FilterTab 
          tabItems={tabItems} 
          className="mb-2" 
          activeValue={
            activeApprovalStatusFilter === "all" ? "all" :
            activeApprovalStatusFilter === 1 ? "approved" :
            activeApprovalStatusFilter === 2 ? "pending" : // "pending" is the value for disapproved
            activeApprovalStatusFilter === 3 ? "rejected" :
            "all"
          }
        />
        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder={t("products.searchPlaceholder")}
                value={searchTerm} // Bind searchTerm to input
                onChange={handleSearchInputChange} // Update searchTerm on change
                onKeyPress={handleSearchKeyPress} // Handle search on Enter
              />
              
              {/* Quantity Range Filter */}
              <div className="d-flex align-items-center gap-2">
                <div className="d-flex align-items-center">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder={t("products.qtyFrom", "Qty From")}
                    style={{ width: "100px" }}
                    value={qtyFromValue}
                    onChange={(e) => setQtyFromValue(e.target.value)}
                    min="0"
                  />
                  <span className="mx-2">-</span>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder={t("products.qtyTo", "Qty To")}
                    style={{ width: "100px" }}
                    value={qtyToValue}
                    onChange={(e) => setQtyToValue(e.target.value)}
                    min="0"
                  />
                </div>
                <Button 
                  variant="warning" 
                  size="sm"
                  onClick={() => {
                    // Apply the quantity range filter
                    setQtyRangeApplied(true);
                    
                    // Reset to first page
                    setPagination(prev => ({
                      ...prev,
                      pageIndex: 0
                    }));
                    
                    // Create filter with qty values directly included
                    const filterWithQty: ProductFilters = {
                      ...getActiveFilters(),
                    };
                    
                    // Explicitly add qty filters to ensure they're included
                    if (qtyFromValue) {
                      filterWithQty.qty_from = parseInt(qtyFromValue);
                    }
                    if (qtyToValue) {
                      filterWithQty.qty_to = parseInt(qtyToValue);
                    }
                    
                    // Log the filter values to verify they're included
                    console.log("Applying qty filter:", filterWithQty);
                    
                    // Fetch products with the quantity range filter
                    fetchProducts({
                      variables: {
                        page: 1,
                        pageSize: pagination.pageSize,
                        filter: filterWithQty,
                      },
                    });
                  }}
                >
                  {t("products.validate", "Validate")}
                </Button>
                {qtyRangeApplied && (
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => {
                      // Clear the quantity range filter
                      setQtyRangeApplied(false);
                      setQtyFromValue("");
                      setQtyToValue("");
                      
                      // Reset to first page
                      setPagination(prev => ({
                        ...prev,
                        pageIndex: 0
                      }));
                      
                      // Fetch products without the quantity range filter
                      fetchProducts({
                        variables: {
                          page: 1,
                          pageSize: pagination.pageSize,
                          filter: getActiveFilters(),
                        },
                      });
                    }}
                  >
                    {t("products.reset", "Reset")}
                  </Button>
                )}
              </div>
              
              <div className="scrollbar overflow-hidden-y">
                <FilterButtonGroup menus={filterMenus} />
              </div>

              {/* Column Visibility Dropdown */}
              <Dropdown>
                <Dropdown.Toggle
                  variant="outline-warning"
                  className="px-7 flex-shrink-0 dropdown-caret-none"
                  style={{
                    backgroundColor: 'transparent',
                    border: '2px solid #ff7f00',
                    color: '#ff7f00',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                  aria-label="Customize table columns"
                >
                  <FontAwesomeIcon icon={faColumns} className="me-2" />
                  {t("products.customizeTable")}
                  <FontAwesomeIcon
                    icon={faAngleDown}
                    className="ms-2"
                    transform="down-2"
                  />
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    padding: '12px',
                    borderRadius: '10px',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                    minWidth: '200px',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* <div className="mb-2 pb-2 border-bottom">
                    <span className="fw-bold text-muted">Toggle Columns</span>
                  </div> */}
                  {table.getAllColumns()
                    .filter(column => typeof column.columnDef.header === "string") // Only include named columns
                    .map((column) => (
                      <ColumnVisibilityToggle
                        key={column.id}
                        id={column.id}
                        label={column.columnDef.header?.toString() || ""}
                        checked={column.getIsVisible()}
                        onChange={(checked) => {
                          column.toggleVisibility(!!checked);
                        }}
                      />
                    ))}
                </Dropdown.Menu>
              </Dropdown>
              <div className="ms-xxl-auto">
                {updateLoading && (
                  <span className="me-3 text-success">
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t("saving")}
                  </span>
                )}
                <Button variant="link" className="text-body me-4 px-0">
                  <FontAwesomeIcon icon={faFileExport} className="fs-9 me-2" />
                  {t("products.export")}
                </Button>
                <Link to="/products/add">
                  <Button variant="warning">
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    {t("addProduct")}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <Spinner animation="border" role="status" variant="warning">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : (
            <ProductsTablecolumns 
              totalCount={paginationCount.totalCount} 
              onShowReasonDetails={handleShowReasonDetails}
              onShowCommissionDetails={handleShowCommissionDetails}
              onInlineEdit={handleInlineEdit}
              editingCell={editingCell}
              editingRow={editingRow}
              editValue={editValue}
              editSpecialPrice={editSpecialPrice}
              editStartDate={editStartDate}
              editEndDate={editEndDate}
            />
            )}
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default Products;
